import type {
  CustomFieldGroup,
  CustomFieldGroupDefinition,
  Resource
} from '__types__';
import {
  fieldSpecUtils,
  type FieldKind,
  type FieldSpec,
  type FieldSpecOf,
  type FieldsUnit,
  type ListUnit
} from 'v5/core/entity-schema/entitySchema.types';
import {
  findGroupMemberRow,
  liveGroupRows
} from 'v5/core/entity-values/codecs/customFieldRows';
import {
  locationValueOf,
  sameLocation
} from 'v5/core/entity-values/codecs/location.codec';
import type {
  GroupMemberWrite,
  GroupRowWrite,
  ResourcePatch
} from 'v5/core/entity-values/resourcePatch.types';
import {
  type CodecContext,
  type CodecRegistry,
  type CoreColumnChange,
  type FieldCodec,
  type FieldWriteMap,
  type ListItemValues,
  type LocationValue,
  type RateWrite,
  type ResourceKinds,
  type UnitValues
} from 'v5/core/entity-values/values.types';
import { readField } from 'v5/core/form-schema/formKinds.types';
import { rowIdUtils } from 'v5/core/form-schema/formRows';

type AnyFieldWrite = FieldWriteMap[FieldKind];

type EncodedField = { spec: FieldSpec; write: AnyFieldWrite; isEmpty: boolean };

/** `spec`'s own kind's codec: it only ever sees that kind's spec and value. */
export function codecOf<K extends FieldKind>(
  codecs: CodecRegistry,
  spec: FieldSpecOf<K>
): FieldCodec<K> {
  return codecs[spec.kind];
}

function encodeField<K extends FieldKind>(
  spec: FieldSpecOf<K>,
  values: UnitValues,
  ctx: CodecContext,
  entity: Resource,
  codecs: CodecRegistry,
  unitId: string
): EncodedField {
  if (!(spec.key in values)) {
    throw new Error(`Unit "${unitId}" has no value for field "${spec.key}"`);
  }
  const codec = codecOf(codecs, spec);
  const value = readField<ResourceKinds, K>(values, spec);
  return {
    spec,
    write: codec.encode(value, spec, ctx, entity),
    isEmpty: codec.isEmpty(value, spec)
  };
}

function encodeFields(
  fields: FieldSpec[],
  values: UnitValues,
  ctx: CodecContext,
  entity: Resource,
  codecs: CodecRegistry,
  unitId: string
): EncodedField[] {
  return fields.map(spec =>
    encodeField(spec, values, ctx, entity, codecs, unitId)
  );
}

/** Merged core changes, or `null` when any field is not a core-changes producer. */
function collectCoreChanges(
  encoded: EncodedField[]
): CoreColumnChange[] | null {
  const changes: CoreColumnChange[] = [];
  for (const { write } of encoded) {
    if (write.type !== 'core-changes') return null;
    changes.push(...write.changes);
  }
  return changes;
}

function describeFields(encoded: EncodedField[]): string {
  return encoded.map(({ spec }) => `${spec.key} (${spec.kind})`).join(', ');
}

/**
 * One group row from its member fields. Members that are empty and have no
 * existing row are skipped; a row that would be inserted without members is
 * dropped (`null`).
 */
function buildGroupRow(
  encoded: EncodedField[],
  rowId: number | null,
  existingRow: CustomFieldGroup | null,
  unitId: string
): GroupRowWrite | null {
  const members: GroupMemberWrite[] = [];
  for (const { spec, write, isEmpty } of encoded) {
    const definition = fieldSpecUtils.definitionOf(spec);
    if (definition === null || write.type !== 'custom') {
      throw new Error(
        `Unit "${unitId}": field "${spec.key}" (${spec.kind}) is not a custom field and cannot be a group member`
      );
    }
    const existingMember =
      existingRow === null
        ? null
        : findGroupMemberRow(existingRow, definition.id, true);
    if (isEmpty && existingMember === null) continue;
    members.push({
      definitionId: definition.id,
      id: existingMember?.id ?? null,
      columns: write.columns
    });
  }
  if (rowId === null && members.length === 0) return null;
  return { id: rowId, members };
}

function groupRowTarget(
  definition: CustomFieldGroupDefinition,
  index: number
): CodecContext['target'] {
  return { type: 'custom-field-group-row', definition, index };
}

/**
 * Encodes one saveable unit into exactly one `ResourcePatch`. The resolver
 * only builds units whose fields share a patch kind; anything else throws.
 */
export function encodeUnit(
  unit: FieldsUnit,
  values: UnitValues,
  entity: Resource,
  codecs: CodecRegistry
): ResourcePatch {
  if (unit.fields.length === 0) {
    throw new Error(`Unit "${unit.id}" has no fields to encode`);
  }

  if (unit.container.type === 'custom-field-group') {
    const definition = unit.container.definition;
    const existingRow = liveGroupRows(entity, definition)[0] ?? null;
    const ctx: CodecContext = {
      scope: unit.scope,
      target: groupRowTarget(definition, 0)
    };
    const encoded = encodeFields(
      unit.fields,
      values,
      ctx,
      entity,
      codecs,
      unit.id
    );
    const row = buildGroupRow(
      encoded,
      existingRow?.id ?? null,
      existingRow,
      unit.id
    );
    return {
      kind: 'custom-field-group',
      definitionId: definition.id,
      scope: unit.scope,
      rows: row === null ? [] : [row]
    };
  }

  const ctx: CodecContext = { scope: unit.scope, target: { type: 'entity' } };
  const encoded = encodeFields(
    unit.fields,
    values,
    ctx,
    entity,
    codecs,
    unit.id
  );

  const coreChanges = collectCoreChanges(encoded);
  if (coreChanges !== null) {
    return { kind: 'core', changes: coreChanges };
  }

  if (encoded.length === 1) {
    const [{ spec, write, isEmpty }] = encoded;
    switch (write.type) {
      case 'core-changes':
        return { kind: 'core', changes: write.changes };
      case 'contact-details':
        return {
          kind: 'contact-details',
          detailType: write.detailType,
          items: write.items
        };
      case 'tags':
        return { kind: 'tags', ids: write.ids, create: write.create };
      case 'groups':
        return { kind: 'groups', ids: write.ids, create: write.create };
      case 'custom': {
        const definition = fieldSpecUtils.definitionOf(spec);
        if (definition === null) {
          throw new Error(
            `Unit "${unit.id}": field "${spec.key}" produced a custom write without a definition`
          );
        }
        return {
          kind: 'custom-field',
          definitionId: definition.id,
          scope: unit.scope,
          write: isEmpty
            ? { type: 'clear' }
            : { type: 'set', columns: write.columns }
        };
      }
      case 'rate':
      case 'location':
        throw new Error(
          `Unit "${unit.id}": ${write.type} field "${spec.key}" must be encoded through encodeList`
        );
    }
  }

  throw new Error(
    `Unit "${unit.id}" mixes fields that cannot form a single patch: ${describeFields(encoded)}`
  );
}

function encodeRatesList(
  unit: ListUnit,
  items: ListItemValues[],
  entity: Resource,
  codecs: CodecRegistry
): ResourcePatch {
  const [spec, ...rest] = unit.item.fields;
  if (spec === undefined || rest.length > 0 || spec.kind !== 'rate') {
    throw new Error(
      `List "${unit.id}" on the entity container must have exactly one rate field`
    );
  }
  const rates: RateWrite[] = items.map((item, index) => {
    const ctx: CodecContext = {
      scope: unit.scope,
      target: { type: 'rates-item', index }
    };
    const [{ write }] = encodeFields(
      [spec],
      item.values,
      ctx,
      entity,
      codecs,
      unit.id
    );
    if (write.type !== 'rate') {
      throw new Error(
        `List "${unit.id}": field "${spec.key}" did not produce a rate write`
      );
    }
    return { ...write.item, id: rowIdUtils.persistedId(item.rowId) };
  });
  return { kind: 'rates', items: rates };
}

function encodeGroupList(
  unit: ListUnit,
  definition: CustomFieldGroupDefinition,
  items: ListItemValues[],
  entity: Resource,
  codecs: CodecRegistry
): ResourcePatch {
  const existingRows = liveGroupRows(entity, definition);
  const rows: GroupRowWrite[] = [];
  items.forEach((item, index) => {
    const rowId = rowIdUtils.persistedId(item.rowId);
    const existingRow =
      rowId === null
        ? null
        : (existingRows.find(row => row.id === rowId) ?? null);
    const rowIndex =
      existingRow === null ? index : existingRows.indexOf(existingRow);
    const ctx: CodecContext = {
      scope: unit.scope,
      target: groupRowTarget(definition, rowIndex)
    };
    const encoded = encodeFields(
      unit.item.fields,
      item.values,
      ctx,
      entity,
      codecs,
      unit.id
    );
    const row = buildGroupRow(encoded, rowId, existingRow, unit.id);
    if (row !== null) rows.push(row);
  });
  return {
    kind: 'custom-field-group',
    definitionId: definition.id,
    scope: unit.scope,
    rows
  };
}

/**
 * Encodes a whole list after a change (item saved / removed / added) into
 * one full-list patch: rates, or the rows of an array custom-field group.
 */
export function encodeList(
  unit: ListUnit,
  items: ListItemValues[],
  entity: Resource,
  codecs: CodecRegistry
): ResourcePatch {
  if (unit.container.type === 'custom-field-group') {
    return encodeGroupList(
      unit,
      unit.container.definition,
      items,
      entity,
      codecs
    );
  }
  const [spec] = unit.item.fields;
  if (spec?.kind === 'location') {
    return encodeLocationsList(unit, items, entity, codecs);
  }
  return encodeRatesList(unit, items, entity, codecs);
}

/**
 * The resource's locations, many-to-many: a saved row still holding its
 * stored location is related by id; a changed one (re-picked or renamed) and
 * a new row create a location from their name and coordinates (a typed name
 * has none). Blank rows are dropped.
 */
function encodeLocationsList(
  unit: ListUnit,
  items: ListItemValues[],
  entity: Resource,
  codecs: CodecRegistry
): ResourcePatch {
  const [spec, ...rest] = unit.item.fields;
  if (spec === undefined || rest.length > 0 || spec.kind !== 'location') {
    throw new Error(
      `List "${unit.id}" of locations must have exactly one location field`
    );
  }
  const stored = new Map(
    (entity.locations ?? []).map(location => [
      location.id,
      locationValueOf(location)
    ])
  );
  const existingIds: number[] = [];
  const create: LocationValue[] = [];
  items.forEach((item, index) => {
    const [{ write }] = encodeFields(
      [spec],
      item.values,
      { scope: unit.scope, target: { type: 'locations-item', index } },
      entity,
      codecs,
      unit.id
    );
    if (write.type !== 'location') {
      throw new Error(
        `List "${unit.id}": field "${spec.key}" did not produce a location write`
      );
    }
    const location = write.value;
    if (location.name.trim() === '') return;
    const id = rowIdUtils.persistedId(item.rowId);
    const original = id === null ? undefined : stored.get(id);
    if (
      id !== null &&
      original !== undefined &&
      sameLocation(location, original)
    ) {
      existingIds.push(id);
      return;
    }
    create.push({
      name: location.name.trim(),
      coordinates: location.coordinates
    });
  });
  return { kind: 'locations', existingIds, create };
}
